import gsap from 'gsap'
import { useRef, useEffect } from 'react'
import { Box, Typography, Grid, Card } from '@mui/material'
import HeroComponent from './components/HeroComponent/HeroComponent'
import ClientComponent from './components/ClientsComponent/ClientComponent'
import ServiceComponent from '../../ServiceComponent'
import StatsComponent from './components/StatsComponent/StatsComponent'
import ProjectsComponent from '../IndexComponent/components/ProjectsComponents'
import FooterComponent from '../FooterComponent/FooterComponent'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ScrollSmoother } from 'gsap/ScrollSmoother'
import HeaderComponent from '../../HomePageComponents/HeaderComponent/HeaderComponent'
import DividerComponent from './DividerComponent'
import { joinus } from '../../../utils/consumo'
import BottomCustom from '../../BottonCustom'
import { useNavigate } from 'react-router-dom'

gsap.registerPlugin(ScrollTrigger, ScrollSmoother)

const IndexComponent = ({ isAuth }) => {
  const main = useRef()
  const smoother = useRef(null)
  const serviceRef = useRef(null)
  const statsRef = useRef(null)
  const projectsAnimRef = useRef(null)
  const joinUsRef = useRef(null)
  const navigate = useNavigate()
  const isInitialized = useRef(false)
  const animationsRef = useRef([])

  const clearRevealStyles = (element, props = 'opacity,transform,filter,scale,y') => {
    if (!element) return
    gsap.set(element, { clearProps: props })
  }

  const revealElements = () => {
    const elementsToReset = [
      serviceRef.current,
      statsRef.current,
      projectsAnimRef.current,
      joinUsRef.current,
    ].filter(Boolean)

    elementsToReset.forEach(element => {
      clearRevealStyles(element)
    })
  }

  const cleanupGSAP = () => {
    animationsRef.current.forEach(anim => {
      if (anim && anim.kill) {
        anim.kill()
      }
    })
    animationsRef.current = []

    ScrollTrigger.getAll().forEach(trigger => {
      trigger.kill()
    })

    if (smoother.current) {
      smoother.current.kill()
      smoother.current = null
    }

    revealElements()

    // Resetear elementos con data-speed
    gsap.utils.toArray('[data-speed]').forEach(element => {
      gsap.set(element, { clearProps: 'all' })
    })

    isInitialized.current = false
  }

  const initializeGSAP = () => {
    if (isInitialized.current) return

    const initTimeout = setTimeout(() => {
      try {
        const wrapper = document.getElementById('smooth-wrapper')
        const content = document.getElementById('smooth-content')

        if (!wrapper || !content) return

        window.scrollTo(0, 0)

        // Crear ScrollSmoother
        smoother.current = ScrollSmoother.create({
          wrapper: '#smooth-wrapper',
          content: '#smooth-content',
          smooth: 1.5,
          effects: true,
          smoothTouch: 0.1,
          normalizeScroll: true,
          ignoreMobileResize: true,
        })

        const elementsWithSpeed = gsap.utils.toArray('[data-speed]:not([data-speed="0"])')

        elementsWithSpeed.forEach(element => {
          const speed = parseFloat(element.getAttribute('data-speed'))
          if (!Number.isFinite(speed) || speed === 1) return

          const parallaxAnim = gsap.fromTo(
            element,
            { y: 0 },
            {
              y: () => -(window.innerHeight * (speed - 1)),
              ease: 'none',
              scrollTrigger: {
                trigger: element,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true,
                invalidateOnRefresh: true,
              },
            }
          )

          animationsRef.current.push(parallaxAnim)
        })

        if (serviceRef.current) {
          const serviceAnim = gsap.fromTo(
            serviceRef.current,
            { opacity: 0.8, scale: 0.98, filter: 'blur(4px)' },
            {
              opacity: 1,
              scale: 1,
              filter: 'blur(0px)',
              duration: 0.8,
              ease: 'power2.out',
              immediateRender: false,
              onComplete: () =>
                clearRevealStyles(serviceRef.current, 'opacity,transform,filter,scale'),
              scrollTrigger: {
                trigger: serviceRef.current,
                start: 'top 85%',
                end: 'bottom 15%',
                scrub: false,
                toggleActions: 'play none none none',
                once: true,
              },
            }
          )
          animationsRef.current.push(serviceAnim)
        }

        // Animación StatsComponent
        if (statsRef.current) {
          const statsAnim = gsap.fromTo(
            statsRef.current,
            { opacity: 0.8, scale: 0.98, filter: 'blur(4px)' },
            {
              opacity: 1,
              scale: 1,
              filter: 'blur(0px)',
              duration: 0.8,
              ease: 'power2.out',
              immediateRender: false,
              onComplete: () =>
                clearRevealStyles(statsRef.current, 'opacity,transform,filter,scale'),
              scrollTrigger: {
                trigger: statsRef.current,
                start: 'top 85%',
                end: 'bottom 15%',
                scrub: false,
                toggleActions: 'play none none none',
                once: true,
              },
            }
          )
          animationsRef.current.push(statsAnim)
        }

        if (projectsAnimRef.current) {
          const projectsAnim = gsap.fromTo(
            projectsAnimRef.current,
            { opacity: 0, y: 50 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: 'power2.out',
              immediateRender: false,
              onComplete: () =>
                clearRevealStyles(projectsAnimRef.current, 'opacity,transform,y'),
              scrollTrigger: {
                trigger: projectsAnimRef.current,
                start: 'top 85%',
                end: 'bottom 10%',
                toggleActions: 'play none none none',
                once: true,
              },
            }
          )
          animationsRef.current.push(projectsAnim)
        }

        if (joinUsRef.current) {
          const joinUsAnim = gsap.fromTo(
            joinUsRef.current,
            { opacity: 0, y: 40 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: 'power2.out',
              immediateRender: false,
              onComplete: () =>
                clearRevealStyles(joinUsRef.current, 'opacity,transform,y'),
              scrollTrigger: {
                trigger: joinUsRef.current,
                start: 'top 90%',
                end: 'bottom 50%',
                toggleActions: 'play none none none',
                once: true,
              },
            }
          )
          animationsRef.current.push(joinUsAnim)
        }

        ScrollTrigger.refresh()

        isInitialized.current = true
      } catch (error) {
        console.error('❌ Error al inicializar GSAP:', error)
        revealElements()
      }
    }, 100)

    return () => clearTimeout(initTimeout)
  }

  useEffect(() => {
    const cleanup = initializeGSAP()

    return () => {
      if (cleanup) cleanup()
      cleanupGSAP()
      window.scrollTo(0, 0)
    }
  }, [])

  useEffect(() => {
    const handleResize = () => {
      if (isInitialized.current) {
        ScrollTrigger.refresh()
      }
    }

    let resizeTimeout
    const debouncedResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(handleResize, 150)
    }

    window.addEventListener('resize', debouncedResize)
    return () => {
      clearTimeout(resizeTimeout)
      window.removeEventListener('resize', debouncedResize)
    }
  }, [])

  return (
    <div id="smooth-wrapper" ref={main} style={{ position: 'relative' }}>
      {/* Imagen de fondo */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          backgroundImage: `url('/images/logo-unl-cropped.svg')`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'contain',
          zIndex: -1,
          opacity: 0.03,
          pointerEvents: 'none',
        }}
      />

      {/* Header fijo */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: 'transparent',
        }}>
        <HeaderComponent isAuth={isAuth} />
      </div>

      <Box
        id="smooth-content"
        sx={{
          backgroundColor: 'rgba(165, 168, 162, 0.08)',
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          perspective: 1000,
          margin: 0,
          padding: 0,
          position: 'relative',
          top: 0,
        }}>
        {/* Hero Component */}
        <Box
          sx={{
            position: 'relative',
            top: 0,
            left: 0,
            right: 0,
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 0,
            padding: 0,
            zIndex: 1,
          }}>
          <HeroComponent
            title="Arbolado Urbano"
            subtitle="Plataforma para el registro, monitoreo, salud, riesgos y servicios ambientales de los árboles urbanos"
          />
        </Box>

        {/* ServiceComponent */}
        <div data-speed="1.05" style={{ willChange: 'transform' }}>
          <DividerComponent
            label="Servicios"
            sx={{
              ml: { xs: 5, sm: 6, md: 6, lg: 8, xl: 10 },
              pt: { xs: 6, sm: 6, md: 7, lg: 8, xl: 9 },
              pb: { md: 2 },
            }}
          />
        </div>
        <Box
          ref={serviceRef}
          sx={{
            willChange: 'transform, filter',
            transform: 'translate3d(0, 0, 0)',
            position: 'relative',
            width: '100%',
            maxWidth: { xs: '100%', sm: '100%', md: 1200, lg: 1500, xl: 2000 },
            mx: 'auto',
          }}>
          <ServiceComponent />
        </Box>

        <div data-speed="1.05" style={{ willChange: 'transform' }}>
          <DividerComponent
            label="Ciudades sostenibles"
            sx={{ pl: { xs: 5, sm: 6, md: 6, lg: 8, xl: 10 }, pt: 4 }}
          />
        </div>

        <div ref={projectsAnimRef} data-speed="1" style={{ willChange: 'transform' }}>
          <ProjectsComponent />
        </div>

        <div style={{ willChange: 'transform' }}>
          <Box
            ref={statsRef}
            sx={{
              transform: 'translate3d(0, 0, 0)',
              position: 'relative',
            }}>
            <div
              style={{
                height: '10px',
                background: `linear-gradient(to right, transparent 0%, rgba(147, 192, 50, 0.9) 100%)`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: '100% 100%',
                willChange: 'transform',
              }}
            />
            <StatsComponent />
            <div
              style={{
                height: '10px',
                background: `linear-gradient(to left, transparent 0%, rgba(147, 192, 50, 0.9) 100%)`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: '100% 100%',
                willChange: 'transform',
              }}
            />
          </Box>
        </div>

        {/* Join Us */}
        <div ref={joinUsRef} style={{ willChange: 'transform' }}>
          <Box>
            <DividerComponent
              label="Súmate +"
              sx={{ pl: { xs: 5, sm: 6, md: 6, lg: 8, xl: 10 }, pb: 4, pt: 4 }}
            />
            <Grid container spacing={3} px={{ xs: 5, sm: 6, md: 6, lg: 8, xl: 9 }}>
              {joinus.map((news, idx) => (
                <Grid item xs={12} sm={12} md={6} key={idx}>
                  <Card
                    sx={{
                      display: 'flex',
                      height: '100%',
                      boxShadow: 3,
                    }}>
                    <Box
                      sx={{
                        flex: 1,
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}>
                      <Typography variant="h6" gutterBottom>
                        {news.title}
                      </Typography>
                      <Typography variant="body2">{news.description}</Typography>
                      {news.botton && (
                        <Box display="flex" justifyContent="end">
                          <BottomCustom
                            styleButton={{ mt: 2, width: 'fit-content' }}
                            text={news.botton.label}
                            onClick={() => navigate(news.botton.redirect)}
                          />
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </div>

        <ClientComponent />
        <FooterComponent />
      </Box>
    </div>
  )
}

export default IndexComponent
